import type {
  BillingPaymentResponse,
  BillingSubscription,
  BillingSubscriptionFee,
  PrimaryPaymentMethod,
} from '../../api/models/bff/Billing';
import type { CardDetails } from '../../models/Billing';
import type { CardMethodDetails } from '../../api/models/bff/Payment';
import type { ReceiptResponse, ReceiptsListResponse } from '../../api/models/bff/Receipt';
import type { PaginationQueryParams } from '../../api/models/bff/Common';
import { generateDateWithDayOffset } from '../utils/generators';

/**
 * Create mock card details (display-friendly, used by UI components)
 *
 * @param overrides - Partial overrides for card details
 * @returns Mock card details
 */
export function createMockCardDetails(overrides?: Partial<CardDetails>): CardDetails {
  return {
    brand: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2028,
    cardholderName: 'John Doe',
    ...overrides,
  };
}

/**
 * Create mock raw card method details (as returned by the API)
 *
 * @param overrides - Partial overrides for card method details
 * @returns Mock card method details
 */
export function createMockCardMethodDetails(overrides?: Partial<CardMethodDetails>): CardMethodDetails {
  return {
    holderName: 'John Doe',
    last4: '4242',
    expiry: { month: 12, year: 2028 },
    brand: 'visa',
    ...overrides,
  };
}

/**
 * Create mock primary payment method
 *
 * @param overrides - Partial overrides for primary payment method
 * @returns Mock primary payment method
 */
export function createMockPrimaryPaymentMethod(overrides?: Partial<PrimaryPaymentMethod>): PrimaryPaymentMethod {
  return {
    id: '550e8400-e29b-41d4-a716-446655440000',
    provider: 'AIRWALLEX',
    method: {
      type: 'CARD',
      details: createMockCardMethodDetails(),
    },
    ...overrides,
  };
}

/**
 * Create mock billing subscription
 *
 * @param overrides - Partial overrides for billing subscription
 * @returns Mock billing subscription
 */
export function createMockBillingSubscription(overrides?: Partial<BillingSubscription>): BillingSubscription {
  const DAYS_UNTIL_NEXT_RENEWAL = 30;
  const DAYS_UNTIL_COMMITMENT_END = 365;
  const DAYS_SINCE_START = 30;

  return {
    id: '550e8400-e29b-41d4-a716-446655440001',
    status: 'ACTIVE',
    displayStatus: 'ACTIVE',
    cancellationEligibility: [],
    plan: {
      code: 'VEL-BASIC-SOLO-OW1-MONTHLY-PLAN',
      addons: [],
      product: {
        code: 'VEL-BASIC',
        family: 'BASIC',
        tripType: 'OW',
      },
      startDate: generateDateWithDayOffset(-DAYS_SINCE_START),
    },
    commitment: {
      amount: 12,
      unit: 'MONTHS',
      endDate: generateDateWithDayOffset(DAYS_UNTIL_COMMITMENT_END),
    },
    price: {
      amount: '123.45',
      currency: 'EUR',
    },
    renewalTerms: {
      frequency: 'MONTHLY',
      billingCycleDay: 1,
      billingAlignment: 'FIXED',
    },
    nextRenewalDate: generateDateWithDayOffset(DAYS_UNTIL_NEXT_RENEWAL),
    effectivePrice: { gross: { amount: '123.45', currency: 'EUR' }, net: { amount: '123.45', currency: 'EUR' } },
    effectivePriceSchedule: [],
    ...overrides,
  };
}

/**
 * Create mock billing subscription fee
 *
 * @param overrides - Partial overrides for billing subscription fee
 * @returns Mock billing subscription fee
 */
export function createMockBillingSubscriptionFee(overrides?: Partial<BillingSubscriptionFee>): BillingSubscriptionFee {
  return {
    type: 'RENEWAL',
    period: null,
    gross: { amount: '123.45', currency: 'EUR' },
    net: { amount: '102.04', currency: 'EUR' },
    additionalCharges: [
      {
        type: 'VAT',
        code: 'VAT_EU',
        amount: { amount: '21.41', currency: 'EUR' },
        rate: { value: '0.21', display: '21%', type: 'PERCENTAGE' },
      },
    ],
    ...overrides,
  };
}

/**
 * Create mock billing payment response
 *
 * @param overrides - Partial overrides for the billing response
 * @returns Mock billing payment response
 */
export function createMockBillingPaymentResponse(overrides?: Partial<BillingPaymentResponse>): BillingPaymentResponse {
  return {
    subscriptions: [createMockBillingSubscription()],
    subscriptionFees: [createMockBillingSubscriptionFee()],
    primaryPaymentMethod: createMockPrimaryPaymentMethod(),
    ...overrides,
  };
}

/**
 * Create a list of mock billing payment responses
 *
 * @param count - Number of responses to generate (default: 1)
 * @param overrides - Partial overrides applied to each response
 * @returns Array of mock billing payment responses
 */
export function createMockBillingPaymentResponseList(
  count = 1,
  overrides?: Partial<BillingPaymentResponse>,
): BillingPaymentResponse[] {
  return Array.from({ length: count }, () => createMockBillingPaymentResponse(overrides));
}

const MOCK_RECEIPTS_COUNT = 23;
const MOCK_RECEIPTS_START_YEAR = 2026;
const MOCK_RECEIPTS_START_MONTH = 6; // June 2026, going backwards

const RECEIPT_STATUSES: Record<number, ReceiptResponse['status']> = {
  11: 'PARTIALLY_PAID',
  14: 'REFUNDED',
  16: 'UNPAID',
};

function createMockReceipt(index: number): ReceiptResponse {
  const monthOffset = index;
  // eslint-disable-next-line no-magic-numbers
  const createdDate = new Date(Date.UTC(MOCK_RECEIPTS_START_YEAR, MOCK_RECEIPTS_START_MONTH - 1 - monthOffset, 1, 10, 30, 0));
  const periodStart = new Date(Date.UTC(createdDate.getUTCFullYear(), createdDate.getUTCMonth() - 1, 1));
  // eslint-disable-next-line no-magic-numbers
  const periodEnd = new Date(Date.UTC(createdDate.getUTCFullYear(), createdDate.getUTCMonth(), 0, 23, 59, 59));

  const year = createdDate.getUTCFullYear();
  // eslint-disable-next-line no-magic-numbers
  const number = `REC-${year}-${String(index + 1).padStart(3, '0')}`;

  // First 2 receipts have a higher price (price increase)
  // eslint-disable-next-line no-magic-numbers
  const isHigherPrice = index < 2;
  const grossAmount = isHigherPrice ? '194.00' : '156.00';
  const netAmount = isHigherPrice ? '160.00' : '128.00';

  const status = RECEIPT_STATUSES[index] ?? 'PAID';

  const periodStartUtc = periodStart.toISOString().replace('.000Z', 'Z');
  const periodEndUtc = periodEnd.toISOString().replace('.000Z', 'Z');

  return {
    id: `d1188ae9-0c52-4902-${String(index).padStart(4, '0')}-8b594c768d55`,
    number,
    createdAt: { utc: createdDate.toISOString().replace('.000Z', 'Z'), zoneId: 'UTC' },
    status,
    payer: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phones: [{ prefix: '+1', number: '5551234567', type: 'MOBILE' }],
      addresses: [{ type: 'HOME', street: '456 Oak Avenue', city: 'Springfield', state: 'IL', postalCode: '62704', country: 'US' }],
    },
    merchant: {
      name: 'Velo Air',
      email: 'support@veloair.com',
      phones: [{ prefix: '+1', number: '234567890', type: 'WORK' }],
      addresses: [{ type: 'BUSINESS', street: '123 Main St', apartment: 'Suite 100', city: 'Anytown', state: 'CA', postalCode: '12345', country: 'US' }],
    },
    period: {
      start: { utc: periodStartUtc, zoneId: 'UTC' },
      end: { utc: periodEndUtc, zoneId: 'UTC' },
    },
    items: [
      {
        code: 'VEL-DUO-BASIC-OW1-MONTHLY-PLAN',
        product: { code: 'DUO-BASIC-OW1-MONTHLY', type: 'BASE', family: 'DUO' },
        period: { start: { utc: periodStartUtc, zoneId: 'UTC' }, end: { utc: periodEndUtc, zoneId: 'UTC' } },
        price: { net: { amount: netAmount, currency: 'USD' }, gross: { amount: grossAmount, currency: 'USD' } },
      },
    ],
    additionalCharges: [],
    subtotal: { net: { amount: netAmount, currency: 'USD' }, gross: { amount: grossAmount, currency: 'USD' } },
    total: { net: { amount: netAmount, currency: 'USD' }, gross: { amount: grossAmount, currency: 'USD' } },
    paid: { amount: grossAmount, currency: 'USD' },
    refunded: { amount: '0.00', currency: 'USD' },
    balance: { amount: '0.00', currency: 'USD' },
  };
}

/**
 * Create mock receipts for the billing receipts table.
 * Generates 17 monthly receipts going back from June 2026.
 *
 * @returns Array of mock receipt responses
 */
export function createMockReceipts(): ReceiptResponse[] {
  return Array.from({ length: MOCK_RECEIPTS_COUNT }, (_, index) => createMockReceipt(index));
}

/**
 * Create a paginated mock receipt list response
 *
 * @param options - Optional pagination parameters
 * @returns Paginated receipt list response
 */
export function createMockReceiptsListResponse(options: PaginationQueryParams = {}): ReceiptsListResponse {
  const allReceipts = createMockReceipts();
  const page = options.page ?? 0;
  const size = options.size ?? allReceipts.length;
  const start = page * size;
  const content = allReceipts.slice(start, start + size);
  const totalElements = allReceipts.length;
  const totalPages = Math.ceil(totalElements / size);

  return {
    content,
    page: {
      size,
      number: page,
      totalElements,
      totalPages,
    },
  };
}
